/**
 * Whether the origin — the Mac mini running the API — is answering.
 *
 * Since the edge proxy, a request can fail without the origin ever seeing it:
 * the VPS terminates TLS, matches the path against its allowlist and hands the
 * bytes to a WebSocket the mini opened, and any of those steps can be the one
 * that is quiet. flows.json `globalRules.originUnreachable` says what to do
 * about it, and it is deliberately global: a user who hits four screens in a
 * row while the tunnel is down should read one problem, not four bugs.
 *
 * So this module owns three things and no screen owns any of them:
 *
 *   1. Detection — api.ts raises the four edge codes here, once, from the one
 *      function every request passes through.
 *   2. Recovery — any successful response proves the origin is back, so the
 *      interstitial retires itself instead of guessing whether a retry worked.
 *   3. Retry — every mounted loader contributes its own reload, so cta.retry
 *      re-issues the requests that failed rather than reloading the page,
 *      which would throw away whatever the screen underneath was holding.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react'
import { setEdgeFailureHandler, setOriginReachedHandler, type ApiFailure } from './api'
import { bridge } from './bridge'
import { isRetryable } from './edge'
import type { InterstitialScreenId } from './contract'

/** The screen the contract names for this condition. Not a route. */
const OUTAGE_SCREEN: InterstitialScreenId = 'serviceUnavailable'

export interface OriginInterstitial {
  id: InterstitialScreenId
  Component: ComponentType
  /** flows.json states for serviceUnavailable: `ready` or `retrying`. */
  state: 'ready' | 'retrying'
}

interface OriginValue {
  /** The edge failure being presented, or null when the origin is answering. */
  outage: ApiFailure | null
  retrying: boolean
  /** True only where re-issuing is known to be safe — see edge.isRetryable. */
  canRetry: boolean
  retry: () => void
  /** Loaders call this while mounted so Retry knows what to re-issue. */
  registerRetry: (reissue: () => void) => () => void
  interstitial: OriginInterstitial | null
}

const OriginContext = createContext<OriginValue | null>(null)

export function OriginProvider({
  screens,
  children,
}: {
  /** From App.tsx — the one place in this surface that maps an id to a component. */
  screens: Record<InterstitialScreenId, ComponentType>
  children: ReactNode
}) {
  const [outage, setOutage] = useState<ApiFailure | null>(null)
  const [retrying, setRetrying] = useState(false)
  const reissuers = useRef(new Set<() => void>())

  useEffect(() => {
    setEdgeFailureHandler((failure) => {
      // A fresh failure ends whatever retry was in flight — including the case
      // where the retry itself is what failed.
      setRetrying(false)
      setOutage(failure)
      bridge.log(`origin: ${failure.body.code} — presenting ${OUTAGE_SCREEN}`)
    })
    setOriginReachedHandler(() => {
      setRetrying(false)
      setOutage(null)
    })
    return () => {
      setEdgeFailureHandler(null)
      setOriginReachedHandler(null)
    }
  }, [])

  const registerRetry = useCallback((reissue: () => void) => {
    reissuers.current.add(reissue)
    return () => {
      reissuers.current.delete(reissue)
    }
  }, [])

  const retry = useCallback(() => {
    // Nothing registered means no in-flight load owns this failure — a mutation
    // did, and re-issuing one of those is the user's call on the screen itself,
    // not something to replay from here. Stand down and hand the screen back.
    if (reissuers.current.size === 0) {
      setOutage(null)
      return
    }
    setRetrying(true)
    for (const reissue of reissuers.current) reissue()
  }, [])

  const value = useMemo<OriginValue>(() => {
    const canRetry = outage ? isRetryable(outage) : false
    return {
      outage,
      retrying,
      canRetry,
      retry,
      registerRetry,
      interstitial: outage
        ? { id: OUTAGE_SCREEN, Component: screens[OUTAGE_SCREEN], state: retrying ? 'retrying' : 'ready' }
        : null,
    }
  }, [outage, retrying, retry, registerRetry, screens])

  return <OriginContext.Provider value={value}>{children}</OriginContext.Provider>
}

/**
 * Optional on purpose. `useAsync` calls it, and useAsync also runs in the
 * render harness under scripts/, which mounts a screen without the app shell.
 * A loader with nowhere to register is not an error; it just cannot be retried
 * from an interstitial that is not there.
 */
export function useOrigin(): OriginValue | null {
  return useContext(OriginContext)
}

/** Inside the provider by construction — the interstitial is its own child. */
export function useOriginOutage(): OriginValue {
  const value = useContext(OriginContext)
  if (!value) throw new Error('useOriginOutage outside OriginProvider')
  return value
}

/** Contribute a way to re-issue this component's request while it is mounted. */
export function useOriginRetry(reissue: () => void): void {
  const origin = useOrigin()
  const register = origin?.registerRetry
  useEffect(() => register?.(reissue), [register, reissue])
}
